/**
 * canvas-engine/renderer/elements/ButtonElement.jsx
 *
 * OWN per-type element renderer for `button` elements.
 *
 * ZERO REUSE: imports NOTHING from template-builder/ or academy-ui/src/template/.
 *
 * Inner-content vs positioning CONTRACT: renders ONLY the inner anchor/button,
 * filling its positioned box. The parent CanvasRenderer owns positioning.
 * DOM output is identical in builder and reader modes. (Req 4.1, 15.1)
 *
 * Pure / presentational. Resilient to missing fields.
 */

import React from 'react';

function str(v) {
  return v == null ? '' : String(v);
}

function color(v, fallback) {
  return typeof v === 'string' && v.length > 0 ? v : fallback;
}

function num(v, fallback) {
  return Number.isFinite(v) ? v : fallback;
}

/**
 * Render a styled link/button.
 *
 * @param {object} props
 * @param {object} props.element normalized `button` Canvas_Element
 */
export default function ButtonElement({ element }) {
  const content = (element && element.content) || {};
  const style = (element && element.style) || {};

  const label = str(content.label) || 'Button';
  const url = str(content.url);
  const newTab = content.newTab === true;
  // linkType is authored metadata ('url' | 'page' | ...); for rendering we use
  // the resolved url string. Kept identical across modes.

  const rootStyle = {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const btnStyle = {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 20px',
    background: color(style.background, '#2563eb'),
    color: color(style.color, '#ffffff'),
    border: `1px solid ${color(style.borderColor, 'transparent')}`,
    borderRadius: num(style.radius, 12),
    fontSize: num(style.fontSize, 16),
    fontWeight: num(style.fontWeight, 600),
    letterSpacing: num(style.letterSpacing, 0),
    textDecoration: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  const link = newTab ? (
    <a
      className="ce-button__link"
      href={url || undefined}
      target="_blank"
      rel="noopener noreferrer"
      style={btnStyle}
    >
      {label}
    </a>
  ) : (
    <a
      className="ce-button__link"
      href={url || undefined}
      style={btnStyle}
    >
      {label}
    </a>
  );

  return (
    <div className="ce-button" style={rootStyle}>
      {link}
    </div>
  );
}
